import { useState, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import {
  GET_MY_LEDGER_REPORTS,
  GET_RESERVE_TRANSACTION_LIST_API,
  POST_RESERVE_TRANSACTION_API
} from './../../../Utils/appConstants';
import { useHistory } from 'react-router-dom';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { getRequest, postRequest } from './../../../Utils/AppApi';
import { Tooltip, Modal, Divider, Button } from 'antd';
import { getUTCToLocalDateTimeFormat } from './../../../Utils/function';
import { INRFormat, isNumber, reserveStatus } from '../../../Utils/Common';
import { toast } from 'react-toastify';

const ReserveBalance = () => {
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [serviceCode, setServiceCode] = useState(null);
  const [serviceName, setServiceName] = useState(null);
  const [isUserId, setUserId] = useState(null);
  const [isData, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModel, setModel] = useState(false);
  const [isModelData, setModelData] = useState(null);
  const [getDataField, setDataField] = useState({});
  const [getDataError, setDataError] = useState({});
  const [isListView, setIsListView] = useState(true);

  const amountInput = useRef(null);
  const transactionIdInput = useRef(null);

  const clearFormField = () => {
    transactionIdInput.current && (transactionIdInput.current.value = '');
    amountInput.current && (amountInput.current.value = '');
    setModelData(null);
    setDataError({});
    setDataField({});
  };

  const FormValidate = () => {
    let formValid = true;
    let formObject = {};
    setDataError({});
    if (getDataField && Object.keys(getDataField).length === 0) {
      formValid = false;
    }
    ['amount', 'transactionId', 'remark'].forEach(element => {
      if (!getDataField[element]) {
        formObject[element] = true;
        formValid = false;
      }
    });
    setDataError(formObject);
    return formValid;
  };

  const objectToQueryString = (obj) => {
    const cleaned = Object.entries(obj).filter(([_, v]) => v !== '' && v !== null && v !== undefined);
    return cleaned.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  };

  const fetchLedger = async (pageNo, limitNo) => {
    setLoading(true);
    const params = {
      limit: limitNo || limit,
      page: pageNo || page
    };
    if (searchText) params.searchTransId = searchText;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    const query = objectToQueryString(params);
    try {
      const response = await getRequest(`${GET_MY_LEDGER_REPORTS}?${query}`, history);
      if (response.success) {
        setData(response.data);
        setTotalRows(response.data?.totalDocs || 0);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error(error);
      setData([]);
      setTotalRows(0);
    }
    setLoading(false);
  };

  const fetchListView = async (pageNo, limitNo) => {
    setLoading(true);
    const params = {
      limit: limitNo || limit,
      page: pageNo || page
    };
    if (searchText) params.searchTransId = searchText;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;

    const query = objectToQueryString(params);
    try {
      const response = await getRequest(`${GET_RESERVE_TRANSACTION_LIST_API}?${query}`, history);
      if (response.success) {
        setData(response.data);
        setTotalRows(response.data?.totalDocs || 0);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error(error);
      setData([]);
      setTotalRows(0);
    }
    setLoading(false);
  };

  // Refetch using currently active view and current filters
  const refreshCurrentView = (newPage, newLimit) => {
    const p = newPage ?? page;
    const l = newLimit ?? limit;
    if (isListView) {
      fetchListView(p, l);
    } else {
      fetchLedger(p, l);
    }
  };

  const handleSearch = () => {
    setPage(1); // reset to first page on new search
    refreshCurrentView(1, limit);
  };

  const handleReset = () => {
    setSearchText('');
    setFromDate('');
    setToDate('');
    setPage(1);
    if (isListView) fetchListView(1, limit);
    else fetchLedger(1, limit);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    refreshCurrentView(newPage, limit);
  };

  const handleRowsPerPageChange = (newLimit, newPage) => {
    setLimit(newLimit);
    setPage(newPage);
    refreshCurrentView(newPage, newLimit);
  };

  const handleListView = () => {
    setIsListView(true);
    setPage(1);
    fetchListView(1, limit);
  };

  const handleLedgerView = () => {
    setIsListView(false);
    setPage(1);
    fetchLedger(1, limit);
  };

  const handleChangeStatus = () => {
    setModel(true);
    if (isData?.docs?.length > 0) {
      const firstDoc = isData.docs[0];
      setServiceCode(firstDoc.serviceCode);
      setServiceName(firstDoc.serviceName);
      setUserId(firstDoc.toUserId);
      setModelData(firstDoc);
      setDataField({
        amount: firstDoc.transactionAmt,
        transactionId: firstDoc.transactionId,
        remark: ''
      });
    } else {
      setModelData(null);
    }
  };

  const handleUnBlock = async (data, status) => {
    setLoading(true);
    const payload = { mode: 'DELETE', status, id: data._id };
    try {
      const response = await postRequest(POST_RESERVE_TRANSACTION_API, payload, history);
      if (response.success) {
        refreshCurrentView(); // refresh without resetting page
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!FormValidate()) return;
    setLoading(true);
    const payload = {
      mode: 'ADD',
      transactionId: getDataField.transactionId,
      serviceCode,
      serviceName,
      amount: getDataField.amount,
      userId: isUserId,
      refId: isModelData?.refId,
      remark: getDataField.remark
    };
    try {
      const response = await postRequest(POST_RESERVE_TRANSACTION_API, payload, history);
      if (response.success) {
        setModel(false);
        toast.success(response.message);
        clearFormField();
        refreshCurrentView();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const onChangeValue = (event) => {
    const { name, value } = event.target;
    setDataField(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    fetchListView(1, limit);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ------- Column definitions (unchanged except minor cleanup) --------
  const columns = [
    { name: 'Date Time', cell: row => getUTCToLocalDateTimeFormat(row.createdAt), width: '200px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Trans ID', selector: row => <Tooltip title={row.transactionId}>{row.transactionId}</Tooltip>, width: '180px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Status', selector: row => '-' },
    { name: 'Ref ID', selector: row => row.toUserData?.username, width: '150px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Service Name', selector: row => <Tooltip title={row.serviceName}>{row.serviceName}</Tooltip>, width: '200px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Product', selector: row => <Tooltip title={row.product}>{row.product}</Tooltip>, width: '200px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Opening Balance', selector: row => row.openingBalance ? INRFormat(row.openingBalance) : '-', width: '150px' },
    { name: 'Debit', selector: row => row.debit ? INRFormat(row.debit) : '-', width: '130px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Credit', selector: row => row.credit ? INRFormat(row.credit) : '-', width: '130px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Ser. Charge', selector: row => row.serviceCharge ? INRFormat(row.serviceCharge) : '-' },
    { name: 'Commission', selector: row => row.commission ? INRFormat(row.commission) : '-' },
    { name: 'TDS', selector: row => row.tds ? INRFormat(row.tds) : '-' },
    { name: 'Closing Balance', selector: row => INRFormat(row.closingBalance), width: '150px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Description', selector: row => row.description ? <Tooltip title={row.description}>{row.description}</Tooltip> : '-', width: '250px' }
  ];

  const columnsListView = [
    { name: 'Date Time', cell: row => getUTCToLocalDateTimeFormat(row.createdAt), width: '200px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Trans ID', selector: row => <Tooltip title={row.transactionId}>{row.transactionId}</Tooltip>, width: '180px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'User ID', selector: row => row.users?.username + ' / ' + row.users?.name, width: '250px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Service Name', selector: row => <Tooltip title={row.serviceName}>{row.serviceName}</Tooltip>, width: '180px', headerStyle: () => ({ textAlign: 'center' }) },
    { name: 'Amount', selector: row => row.amount ? INRFormat(row.amount) : '-', width: '100px' },
    { name: 'Status', selector: row => row.status ? reserveStatus(row.status) : '-', width: '100px' },
    {
      name: 'Action',
      selector: row => row.status === '0' ? (
        <>
          <Button type="primary" danger onClick={() => handleUnBlock(row, '2')}>Block</Button>
          <Button type="primary" className="ml-1" onClick={() => handleUnBlock(row, '1')}>Release</Button>
        </>
      ) : '-',
      width: '230px',
      headerStyle: () => ({ textAlign: 'center' })
    },
    { name: 'Remark', selector: row => row.remark ? <Tooltip title={row.remark}>{row.remark}</Tooltip> : '-', width: '250px' }
  ];

  if (isLoading) {
    return (
      <div className="content text-center" style={{ margin: '250px 0' }}>
        <PropagateLoader color="#5851D2" loading={true} size={25} />
      </div>
    );
  }

  return (
    <div>
      <div className="">
        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body vh-100">
                <div className="row mb-3">
                  {/* Search & filter controls */}
                  <div className="col-md-8">
                    <div className="input-group">
                      <input
                        className="form-control py-2 border"
                        type="text"
                        value={searchText}
                        placeholder="Transaction ID"
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <input
                        type="date"
                        className="form-control ml-2"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="From Date"
                      />
                      <input
                        type="date"
                        className="form-control ml-2"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="To Date"
                      />
                      <button onClick={handleSearch} className="btn btn-primary pointer ml-2">Search</button>
                      <button onClick={handleReset} className="btn btn-secondary pointer ml-2">Reset</button>
                    </div>
                  </div>
                  <div className="col-md-4 text-right">
                    <button onClick={handleListView} className="btn btn-success pointer ml-2">Reserve List View</button>
                    <button onClick={handleLedgerView} className="btn btn-info pointer ml-2">Ledger View</button>
                    <button onClick={handleChangeStatus} className="btn btn-warning pointer ml-2">Reserve Amount</button>
                  </div>
                </div>

                <Divider />

                <div className="container-fluid pt-2 pb-5">
                  <DataTable
                    title=""
                    columns={isListView ? columnsListView : columns}
                    data={isData?.docs || []}
                    progressPending={isLoading}
                    highlightOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={limit}
                    paginationDefaultPage={page}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                  />
                </div>

                {/* Modal unchanged */}
                {isModel && (
                  <Modal title="Transaction Details" width={600} open={isModel} onCancel={() => setModel(false)} footer={null}>
                    <div className="row">
                      <div className="col-md-12 form-group">
                        <div className="project-info-box text-primary">
                          <div className="row col-md-12">
                            <div className="col-md-6">
                              <p><b>User ID :</b> {isModelData?.toUserData?.username}</p>
                              <p><b>Name :</b> {isModelData?.toUserData?.name}</p>
                              <p><b>Mobile :</b> {isModelData?.toUserData?.mobile}</p>
                              <p><b>Transaction Id :</b> {isModelData?.transactionId}</p>
                            </div>
                            <div className="col-md-6">
                              <p><b>Amount :</b> {isModelData?.transactionAmt}</p>
                              <p><b>Commission :</b> {isModelData?.commission}</p>
                              <p><b>Service Charge :</b> {isModelData?.serviceCharge}</p>
                              <p><b>TDS :</b> {isModelData?.tds}</p>
                            </div>
                          </div>
                          <Divider />
                          <div className="row col-md-12">
                            <div className="form-group col-md-6">
                              <label>Amount</label>
                              <input
                                type="text"
                                className={`form-control normal-light ${getDataError?.amount ? 'is-invalid' : ''}`}
                                name="amount"
                                maxLength={12}
                                value={getDataField?.amount || ''}
                                onKeyDown={isNumber}
                                onChange={onChangeValue}
                                ref={amountInput}
                                placeholder="Amount"
                              />
                              {getDataError?.amount && <span className="error text-danger">The field is required</span>}
                            </div>
                            <div className="form-group col-md-6">
                              <label>Transaction Id</label>
                              <input
                                type="text"
                                className={`form-control normal-light ${getDataError?.transactionId ? 'is-invalid' : ''}`}
                                name="transactionId"
                                value={getDataField?.transactionId || ''}
                                onChange={onChangeValue}
                                ref={transactionIdInput}
                                placeholder="Transaction Id"
                              />
                              {getDataError?.transactionId && <span className="error text-danger">The field is required</span>}
                            </div>
                          </div>
                          <div className="form-group col-md-12">
                            <label>Remark</label>
                            <textarea
                              className={`form-control normal-light ${getDataError?.remark ? 'is-invalid' : ''}`}
                              name="remark"
                              onChange={onChangeValue}
                              rows={5}
                              value={getDataField?.remark || ''}
                            />
                            {getDataError?.remark && <span className="error text-danger">The field is required</span>}
                          </div>
                          <div className="float-right">
                            <button onClick={handleSubmit} className="btn btn-primary pointer ml-3">Submit</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Modal>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReserveBalance;